import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import AntLocalization from '../../utils/AntLocalization';
import MiniCalendar from './MiniCalendar';
import SlotGrid from './SlotGrid';
import RangeMode from './RangeMode';
import AntCondition from '../../utils/AntCondition';
import './index.scss';

// ─── Mock Data ───────────────────────────────────────────────────
var MOCK_RESOURCES = [
    { code: 'staff-1', name: 'Nguyễn Thị Lan', type: 'Stylist' },
    { code: 'staff-2', name: 'Trần Văn Minh', type: 'Stylist' },
    { code: 'staff-3', name: 'Phạm Hồng Anh', type: 'Therapist' },
    { code: 'staff-4', name: 'Lê Thúy Hằng', type: 'Therapist' }
];

var MOCK_BOOKINGS = (function () {
    var today = new Date(); today.setHours(0, 0, 0, 0);
    var todayStr = today.toISOString().split('T')[0];
    return [
        { id: 'bk1', date: todayStr, time: 8, end_time: 9.5, resource_code: 'staff-1', entity_name: 'Chị Mai', status: 'Close' },
        { id: 'bk2', date: todayStr, time: 10, end_time: 11.5, resource_code: 'staff-1', entity_name: 'Anh Tuấn', status: 'Open' },
        { id: 'bk3', date: todayStr, time: 9, end_time: 10.5, resource_code: 'staff-2', entity_name: 'Chị Hoa', status: 'Release' },
        { id: 'bk4', date: todayStr, time: 13, end_time: 14.5, resource_code: 'staff-3', entity_name: 'Chị Linh', status: 'New' },
        { id: 'bk5', date: todayStr, time: 14, end_time: 16, resource_code: 'staff-4', entity_name: 'Anh Khoa', status: 'Release' }
    ];
})();

var MOCK_OCCUPANCY = (function () {
    var data = {};
    var today = new Date();
    for (var i = -5; i < 25; i++) {
        var d = new Date(today); d.setDate(d.getDate() + i);
        var key = d.toISOString().split('T')[0];
        data[key] = Math.round(Math.random() * 80 + 10);
    }
    return data;
})();

function formatDateISO(d) {
    if (!d) return '';
    var yyyy = d.getFullYear();
    var mm = d.getMonth() + 1;
    var dd = d.getDate();
    return yyyy + '-' + (mm < 10 ? '0' : '') + mm + '-' + (dd < 10 ? '0' : '') + dd;
}

function formatDateDisplay(d) {
    if (!d) return '';
    var lang = 'vi';
    try { lang = AntLocalization.getLanguage() || 'vi'; } catch (e) { /* */ }

    var dayNames = {
        vi: ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'],
        en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    };
    var names = dayNames[lang] || dayNames.vi;
    var dd = d.getDate();
    var mm = d.getMonth() + 1;
    var yyyy = d.getFullYear();
    return names[d.getDay()] + ', ' + (dd < 10 ? '0' : '') + dd + '/' + (mm < 10 ? '0' : '') + mm + '/' + yyyy;
}

// ═══════════════════════════════════════════════════════════════
// ─── MAIN COMPONENT ──────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════
export default function AntBookingCalendar(props) {
    var {
        // Data
        API,
        resourceAPI,
        session,

        // Fields mapping
        dateField = 'appointment_date',
        timeField = 'appointment_hour',
        endTimeField,
        resourceField = 'serve_entity_code',
        statusField = 'appointment_status',
        displayField = 'entity_name',

        // Display
        title,
        mode = 'slot',
        durationType = 'DAILY',
        slotDuration = 30,
        startHour = 7,
        endHour = 22,
        statusColors,
        showOccupancy = true,
        showMiniCalendar = true,
        compact = false,

        // Resource
        defaultResource,
        filterByResource,
        resourceFilterField,

        // Events
        onSlotSelect,
        onBookingClick,
        onBookResource,
        onDateChange,

        // Filtering
        searchConditions,

        // Mock & visibility
        mockMode = false,
        visible
    } = props;

    var [selectedDate, setSelectedDate] = useState(function () { return new Date(); });
    var [resources, setResources] = useState([]);
    var [bookings, setBookings] = useState([]);
    var [occupancyData, setOccupancyData] = useState(null);
    var [loading, setLoading] = useState(false);
    var [selectedResource, setSelectedResource] = useState(defaultResource || '');
    var mountedRef = useRef(true);

    useEffect(function () {
        return function () { mountedRef.current = false; };
    }, []);

    // ─── Load resources ─────────────────────────────────────────
    var loadResources = useCallback(async function () {
        if (mockMode) {
            if (mountedRef.current) setResources(MOCK_RESOURCES);
            return;
        }
        if (!resourceAPI) return;
        try {
            var params = { pageSize: 200 };
            if (filterByResource) {
                params.searchConditions = filterByResource;
            }
            var result = await resourceAPI.getList(params);
            if (result && result.rows && mountedRef.current) {
                setResources(result.rows);
            }
        } catch (e) {
            // silent
        }
    }, [resourceAPI, filterByResource, mockMode]);

    useEffect(function () {
        loadResources();
    }, [loadResources]);

    // ─── Load bookings for selected date ────────────────────────
    var loadBookings = useCallback(async function () {
        if (mockMode) {
            if (mountedRef.current) setBookings(MOCK_BOOKINGS);
            return;
        }
        if (!API) return;
        setLoading(true);
        try {
            var params = { pageSize: 500 };
            var conditions = {};
            if (searchConditions) {
                conditions = Object.assign({}, searchConditions);
            }

            if (mode === 'slot') {
                // Load single day
                conditions[dateField] = formatDateISO(selectedDate);
            } else {
                // Range mode: load wider range (current month ± buffer)
                var startDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
                var endDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 2, 0);
                conditions[dateField] = {
                    type: 'between',
                    from: formatDateISO(startDate),
                    to: formatDateISO(endDate)
                };
            }

            // Exclude cancelled
            if (!conditions[statusField]) {
                conditions[statusField] = { type: '!=', value: 'Cancel' };
            }

            // Filter by resource if selected
            if (selectedResource && resourceField) {
                conditions[resourceField] = selectedResource;
            }

            params.searchConditions = conditions;
            var result = await API.getList(params);
            if (result && result.rows && mountedRef.current) {
                setBookings(result.rows);
            }
        } catch (e) {
            // silent
        }
        if (mountedRef.current) setLoading(false);
    }, [API, selectedDate, searchConditions, statusField, dateField, resourceField, selectedResource, mode, mockMode]);

    useEffect(function () {
        loadBookings();
    }, [loadBookings]);

    // ─── Load occupancy data for mini calendar ──────────────────
    var loadOccupancy = useCallback(async function () {
        if (mockMode) {
            if (mountedRef.current) setOccupancyData(MOCK_OCCUPANCY);
            return;
        }
        if (!API || !showOccupancy) return;
        try {
            var year = selectedDate.getFullYear();
            var month = selectedDate.getMonth();
            var startDate = new Date(year, month, 1);
            var endDate = new Date(year, month + 1, 0);

            var params = {
                searchConditions: {},
                groupBy: dateField,
                pageSize: 50
            };
            if (searchConditions) {
                params.searchConditions = Object.assign({}, searchConditions);
            }
            params.searchConditions[dateField] = {
                type: 'between',
                from: formatDateISO(startDate),
                to: formatDateISO(endDate)
            };
            if (!params.searchConditions[statusField]) {
                params.searchConditions[statusField] = { type: '!=', value: 'Cancel' };
            }

            var result = await API.count(params);
            if (!result || !mountedRef.current) return;

            // Build occupancy map
            // Assume max capacity = resources.length * (work hours / slot duration)
            var slotsPerDay = resources.length > 0
                ? resources.length * Math.floor((endHour - startHour) * 60 / slotDuration)
                : 20; // fallback

            var occMap = {};
            if (result.rows) {
                for (var i = 0; i < result.rows.length; i++) {
                    var row = result.rows[i];
                    var dateKey = row[dateField];
                    var count = row.count || row.total || 0;
                    occMap[dateKey] = Math.min(100, Math.round(count / slotsPerDay * 100));
                }
            } else if (typeof result === 'number') {
                // Single count — put on selected date
                var key = formatDateISO(selectedDate);
                occMap[key] = Math.min(100, Math.round(result / slotsPerDay * 100));
            }

            setOccupancyData(occMap);
        } catch (e) {
            // silent — occupancy is optional
        }
    }, [API, showOccupancy, selectedDate, resources, searchConditions, dateField, statusField, startHour, endHour, slotDuration, mockMode]);

    useEffect(function () {
        loadOccupancy();
    }, [loadOccupancy]);

    // ─── Date selection ─────────────────────────────────────────
    var handleDateSelect = useCallback(function (date) {
        setSelectedDate(date);
        if (onDateChange) onDateChange(date);
    }, [onDateChange]);

    // ─── Resource filter ────────────────────────────────────────
    var handleResourceFilter = useCallback(function (e) {
        setSelectedResource(e.target.value);
    }, []);

    // ─── Filtered resources for display ─────────────────────────
    var displayResources = useMemo(function () {
        if (!selectedResource) return resources;
        return resources.filter(function (r) {
            return (r.id || r.code) === selectedResource;
        });
    }, [resources, selectedResource]);

    // ─── Enriched resources for SlotGrid ────────────────────────
    var slotResources = useMemo(function () {
        return displayResources.map(function (r) {
            return Object.assign({}, r, {
                _selectedDate: selectedDate,
                _slotDuration: slotDuration / 60
            });
        });
    }, [displayResources, selectedDate, slotDuration]);

    // ─── Title ──────────────────────────────────────────────────
    var displayTitle = title || (mode === 'slot'
        ? AntLocalization.translateText({ vi: 'Đặt lịch hẹn', en: 'Book Appointment', fr: 'Prendre rendez-vous', es: 'Reservar cita', id: 'Buat janji temu', ja: '予約を作成', zh: '预约', ko: '예약하기', hi: 'अपॉइंटमेंट बुक करें', ar: 'حجز موعد' })
        : AntLocalization.translateText({ vi: 'Đặt phòng', en: 'Book Room', fr: 'Réservation', es: 'Reservar', id: 'Pesan kamar', ja: '客室予約', zh: '订房', ko: '객실 예약', hi: 'कमरा बुकिंग', ar: 'حجز غرفة' }));

    // ─── Default status colors ──────────────────────────────────
    var colors = statusColors || {
        'New': '#94a3b8',
        'Release': '#3b82f6',
        'Open': '#f59e0b',
        'Close': '#10b981',
        'Closed': '#10b981',
        'Cancel': '#ef4444'
    };

    // ─── Visibility check (after all hooks) ─────────────────────
    if (visible && session && !AntCondition.checkAll({}, visible, session)) return null;

    // ─── Render ─────────────────────────────────────────────────
    var rootClass = 'ant-booking-calendar'
        + (compact ? ' compact' : '')
        + (mode === 'range' ? ' mode-range' : ' mode-slot');

    return (
        <div className={rootClass}>
            {/* Header */}
            <div className="booking-header">
                <div className="header-left">
                    <span className="header-title">{displayTitle}</span>
                    {mode === 'slot' && (
                        <span className="header-date">{formatDateDisplay(selectedDate)}</span>
                    )}
                    {loading && <span className="header-loading">...</span>}
                </div>
                <div className="header-right">
                    {/* Resource filter */}
                    {resources.length > 1 && mode === 'slot' && (
                        <div className="resource-filter">
                            <select
                                className="resource-select"
                                value={selectedResource}
                                onChange={handleResourceFilter}
                            >
                                <option value="">
                                    {AntLocalization.translateText({
                                        vi: 'Tất cả ' + (resourceFilterField === 'place' ? 'phòng' : 'nhân viên'),
                                        en: 'All ' + (resourceFilterField === 'place' ? 'rooms' : 'staff')
                                    })}
                                </option>
                                {resources.map(function (r) {
                                    var rId = r.id || r.code;
                                    return (
                                        <option key={rId} value={rId}>{r.name || rId}</option>
                                    );
                                })}
                            </select>
                        </div>
                    )}
                    {/* Refresh */}
                    <button
                        className="refresh-btn"
                        onClick={function () { loadBookings(); if (showOccupancy) loadOccupancy(); }}
                        disabled={loading}
                        title={AntLocalization.translateText({ vi: 'Làm mới', en: 'Refresh', fr: 'Actualiser', es: 'Actualizar', id: 'Muat ulang', ja: '更新', zh: '刷新', ko: '새로고침', hi: 'रीफ़्रेश', ar: 'تحديث' })}
                    >
                        {loading ? '...' : '↻'}
                    </button>
                </div>
            </div>

            {/* Body */}
            <div className="booking-body">
                {/* Mini Calendar (left sidebar) */}
                {showMiniCalendar && (
                    <div className="booking-sidebar">
                        <MiniCalendar
                            selectedDate={selectedDate}
                            onDateSelect={handleDateSelect}
                            occupancyData={occupancyData}
                        />

                        {/* Quick stats */}
                        {mode === 'slot' && bookings.length > 0 && (
                            <div className="sidebar-stats">
                                <div className="stat-row">
                                    <span className="stat-label">
                                        {AntLocalization.translateText({ vi: 'Tổng lịch hẹn', en: 'Total bookings', fr: 'Réservations', es: 'Total reservas', id: 'Total booking', ja: '予約合計', zh: '预约总数', ko: '총 예약', hi: 'कुल बुकिंग', ar: 'إجمالي الحجوزات' })}
                                    </span>
                                    <span className="stat-value">{bookings.length}</span>
                                </div>
                                {resources.length > 0 && (
                                    <div className="stat-row">
                                        <span className="stat-label">
                                            {AntLocalization.translateText({ vi: 'Tài nguyên', en: 'Resources', fr: 'Ressources', es: 'Recursos', id: 'Sumber daya', ja: 'リソース', zh: '资源', ko: '리소스', hi: 'संसाधन', ar: 'الموارد' })}
                                        </span>
                                        <span className="stat-value">{displayResources.length}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Status legend */}
                        <div className="sidebar-legend">
                            {Object.keys(colors).map(function (status) {
                                if (status === 'Cancel') return null;
                                return (
                                    <div key={status} className="legend-item">
                                        <span className="legend-dot" style={{ background: colors[status] }} />
                                        <span className="legend-label">{status}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Main content area */}
                <div className="booking-main">
                    {mode === 'slot' ? (
                        <SlotGrid
                            selectedDate={selectedDate}
                            resources={slotResources}
                            bookings={bookings}
                            startHour={startHour}
                            endHour={endHour}
                            slotDuration={slotDuration}
                            statusField={statusField}
                            timeField={timeField}
                            endTimeField={endTimeField}
                            resourceField={resourceField}
                            statusColors={colors}
                            onSlotSelect={onSlotSelect}
                            onBookingClick={onBookingClick}
                        />
                    ) : (
                        <RangeMode
                            resources={resources}
                            bookings={bookings}
                            durationType={durationType}
                            resourceField={resourceField}
                            statusColors={colors}
                            statusField={statusField}
                            onBookResource={onBookResource}
                            onBookingClick={onBookingClick}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
